import { MappedFields } from '@/lib/components/visual-editor/component/component-wrapper';
import { TTSchemaWithSubschemas } from '@/lib/components/visual-editor/dynamic-data/field-list';
import DummyValueFactory, {
  IDummyFieldValueFactory,
} from '@/lib/components/visual-editor/field-type/DummyValueFactory';
import { SelectionLevels } from '@/lib/components/visual-editor/hooks/useBlockNavigationDepth';
import {
  CreatePageRevisionInput,
  CreateSchemaBlockMutation,
  CreateSchemaBlockMutationVariables,
  PageRevisionEntityDto,
  PageRevisionSchemaEntityDto,
  PageRevisionSchemaInput,
  PageRevisionSchemaProperties,
  PageRevisionSchemaPropertiesInput,
  RevisionSchemaSchemaDto,
  RevisionSchemaSchemaFieldDto,
  TtSchemaFieldEntityDto,
  TemplateEntityDto,
  TtSchemaFieldType,
} from '@/lib/types/graphql';
import {
  FieldMap,
  MAX_SCHEMA_RECURSION_DEPTH,
  ReferenceList,
  TTSchemaField,
  TTSchemaFieldType,
  TTSchemaFieldVisibility,
  validateFieldPropertySchemaValue,
  ValidationError,
  ValidationErrorSeverity,
} from '@twotaps/all-shared';
import _, { set } from 'lodash';
import { Client } from 'urql';
import { CREATE_SCHEMA_BLOCK } from '../queries/schema.query';
import * as yup from 'yup';

type Props = {
  domainName?: string;
  ssl?: boolean;
  path?: string | null | undefined;
};

export type DomainURL = Props | null | undefined;
export function getDomainURL(domain: DomainURL, includePath = true) {
  if (!domain) {
    return '';
  }
  const protocol = domain.ssl ? 'https' : 'http';
  const domainURL = `${protocol}://${domain.domainName}`;

  if (!includePath && domain.path) {
    return domainURL.replace(`/${domain.path}`, '');
  }
  return domainURL;
  // return `${protocol}://${domain.domainName}${includePath && domain.path ? `/${domain.path}` : ''}`;
}

type SchemaProperties = {
  fieldId: number;
  name: string;
  value: any | any[];
};

export type KeyedPageRevisionSchemaProperties = {
  [key: string]: PageRevisionSchemaProperties;
};

export type SchemaFieldMap = Map<number, Pick<TtSchemaFieldEntityDto, 'type'>>;

export const mapPropertiesToFieldNames = (prev: SchemaProperties[], property: SchemaProperties) => {
  let value: any | any[] = [];
  if (property.value && property.value.length === 1) {
    value = property.value[0];
  } else if (property.value && property.value.length > 1) {
    value = property.value;
  }
  value = mapSubschemaField(value);
  return { ...prev, [property.fieldId]: value };
};

/**
 * Map subschema field values to fieldId
 *
 * @param value
 * @returns
 */
function mapSubschemaField(value: any) {
  if (Array.isArray(value) && value.length > 0 && value[0][0].fieldId) {
    return value.map((val) => val.reduce(mapPropertiesToFieldNames, {} as MappedFields));
  }

  if (value.fieldId) {
    return { [value.fieldId]: value.value };
  }
  return value;
}

const NON_REVERTED_FIELD_TYPES = [
  TtSchemaFieldType.GmbLocation,
  TtSchemaFieldType.Instagram,
  TtSchemaFieldType.Link,
  TtSchemaFieldType.Image,
];

export function revertFieldMapping(
  mappedFieldProperties: KeyedPageRevisionSchemaProperties,
  schemaFieldMap: SchemaFieldMap,
) {
  const revertedFieldProperties = Object.keys(mappedFieldProperties)
    .filter((field) => mappedFieldProperties[field] !== undefined)
    .map((key) => mappedFieldProperties[key]);

  revertedFieldProperties.forEach((fieldProperty) => {
    const schemaField = fieldProperty && schemaFieldMap.get(fieldProperty.fieldId);
    if (
      fieldProperty &&
      fieldProperty.value &&
      fieldProperty.value.length > 0 &&
      typeof fieldProperty.value[0] === 'object' &&
      (!schemaField?.type || !NON_REVERTED_FIELD_TYPES.includes(schemaField.type))
    ) {
      fieldProperty.value = fieldProperty.value.map((val: KeyedPageRevisionSchemaProperties) =>
        val !== null ? revertFieldMapping(val, schemaFieldMap) : null,
      );
    }
  });
  return revertedFieldProperties;
}

/**
 * Path to the field
 *
 * @param selectionLevel
 * @returns
 */
export function fieldPath(selectionLevel: SelectionLevels[]): string {
  let path: string =
    selectionLevel.length > 0
      ? `${selectionLevel[0].fieldId}.value${
          typeof selectionLevel[0].fieldIndex !== 'undefined'
            ? `[${selectionLevel[0].fieldIndex}]`
            : ''
        }`
      : '';

  path += selectionLevel
    .slice(1, selectionLevel.length)
    .reduce(
      (prev, sl) =>
        `${prev ?? `${prev}.value`}.${sl.fieldId}.value${
          typeof sl.fieldIndex !== 'undefined' ? `[${sl.fieldIndex}]` : ''
        }`,
      '',
    );
  return path;
}

/**
 * @deprecated In favour of Property method from lodash
 * @param selectedLevel
 * @param values
 * @returns
 */
export function resolveFieldPath(selectedLevel: SelectionLevels[], values: any) {
  let value: any = _.cloneDeep(values);
  const levels = _.cloneDeep(selectedLevel);
  levels.forEach((level) => {
    if (level.fieldId && value[level.fieldId]) {
      value = value[level?.fieldId];
      if (typeof level.fieldIndex === 'number') {
        value = value.value[level.fieldIndex];
      }
    }
  });
  return value;
}

/**
 * Recursively map propeties by fieldId
 * It helps with formik, as we are relaying on Formik for field updates.
 *
 * @param values
 * @returns
 */
export function mapProperties(
  values: PageRevisionSchemaProperties[],
): KeyedPageRevisionSchemaProperties {
  const keyedValues = _.keyBy(values, 'fieldId');
  Object.keys(keyedValues).forEach((key) => {
    if (
      keyedValues[key]?.value &&
      keyedValues[key]?.value.length > 0 &&
      Array.isArray(keyedValues[key].value[0])
    ) {
      keyedValues[key].value = keyedValues[key].value.map((val: PageRevisionSchemaProperties[]) =>
        mapProperties(val),
      );
    }
  });
  return keyedValues;
}

export type DashboardPageRevisionSchema = PageRevisionSchemaEntityDto & {
  properties: KeyedPageRevisionSchemaProperties;
};

export function getRevisionSchemaDefinition(revisionSchemas: DashboardPageRevisionSchema[]) {
  const schemas = new Map<number, RevisionSchemaSchemaDto>();
  const fields = new Map<number, RevisionSchemaSchemaFieldDto>();
  revisionSchemas.forEach((revisionSchema) => {
    const mainSchema = revisionSchema.ttschema;
    if (!mainSchema) {
      return;
    }
    [mainSchema, ...mainSchema.subSchemas].forEach((subSchema) => {
      schemas.set(subSchema.id, { id: subSchema.id, type: subSchema.type });
      subSchema.fields.forEach((field) => {
        fields.set(field.id, {
          id: field.id,
          hasMultipleValues: field.hasMultipleValues,
          type: field.type,
          schemaId: field.TTSchema?.id || 0,
          schemaTypeId: field.typeTTSchema?.id,
        });
      });
    });
  });
  return {
    schemas: Array.from(schemas.values()),
    fields: Array.from(fields.values()),
  };
}

/**
 * Format page schema to revision input
 *
 * @param revision PageRevision
 * @param publish Publish the revision? Default: No
 * @param scheduledAt scheduled time for publishing. Default: undefined
 * @returns
 */
export const formatRevisionInput = (
  revisionSchemas: DashboardPageRevisionSchema[],
  publish: boolean = false,
  scheduledAt: Date | undefined = undefined,
): Pick<CreatePageRevisionInput, 'revisionSchemas' | 'publish' | 'scheduledAt'> => {
  const schemas = revisionSchemas
    .filter((revisionSchema) => !(revisionSchema.inHeader || revisionSchema.inFooter))
    .reduce((accum, revisionSchema) => {
      if (
        (!revisionSchema.ttschema && !revisionSchema.template && !revisionSchema.campaign) ||
        !revisionSchema.properties
      ) {
        return accum;
      }
      const properties = revisionSchema.properties;
      accum.push({
        anchorId: revisionSchema.anchorId,
        label: revisionSchema.label,
        hideBlockForCampaignPlaceholders: revisionSchema.hideBlockForCampaignPlaceholders,
        sortOrder: revisionSchema.sortOrder,
        templateId: revisionSchema.template?.id,
        ttschemaId: revisionSchema.ttschema?.id,
        campaign: revisionSchema.campaign,
        schemaBlockId: revisionSchema.schemaBlockId,
        properties: properties.reduce(
          (
            arr: PageRevisionSchemaPropertiesInput[],
            property: PageRevisionSchemaPropertiesInput,
          ) => [
            ...arr,
            {
              fieldId: property.fieldId,
              name: property.name,
              value: property.value,
              isDynamic: property.isDynamic,
              valueReferences: property.valueReferences,
              ...(property.isDynamic && { dynamicReference: property.dynamicReference }),
            },
          ],
          [] as PageRevisionSchemaPropertiesInput[],
        ),
      });
      return accum;
    }, [] as PageRevisionSchemaInput[]);
  return {
    publish,
    scheduledAt,
    revisionSchemas: schemas ?? [],
  };
};

/**
 * Generate field properties for new block
 *
 * @param ttschema  TTSchema
 * @param subschemas
 * @returns PageSchema Properties
 */
export function generateFieldProperties(
  ttschema?: Partial<Pick<TTSchemaWithSubschemas, 'fields' | 'subSchemas'>> | null,
  subschemas?: Partial<Pick<TTSchemaWithSubschemas, 'id' | 'fields'>>[] | null,
): PageRevisionSchemaProperties[] {
  if (!subschemas) {
    subschemas = ttschema?.subSchemas;
  }
  return (
    ttschema?.fields?.map((field) => {
      const subschema = subschemas?.find((subschema) => subschema.id === field.typeTTSchema?.id);
      if (field.type === TtSchemaFieldType.Schema && subschema) {
        return {
          ...DummyValueFactory.createField(field),
          value: [generateFieldProperties(subschema, subschemas)],
        };
      }
      if (DummyValueFactory[field.type as keyof IDummyFieldValueFactory]) {
        return DummyValueFactory[field.type as keyof IDummyFieldValueFactory](field);
      }

      return {
        fieldId: field.id,
        name: field.name,
        value: field.hasMultipleValues ? [] : [''],
        isDynamic: false,
      };
    }) || []
  );
}

/**
 * Formats the file size in bytes to relevant size
 * @param bytes
 * @returns
 */
export function formatBytes(bytes: number) {
  if (bytes === 0 || !bytes) {
    return '0';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** sizeIndex).toFixed(2) ?? ''}  ${sizes[sizeIndex]}`;
}

function getFilenameFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.split('/').pop() || '';
  } catch {
    return '';
  }
}

interface ImageProperties {
  width: number;
  height: number;
  fileName: string;
}

/**
 * Get the dimensions of the image
 *
 * @param base64
 */
export async function getImageDimensions(url: string): Promise<ImageProperties> {
  return new Promise((resolve) => {
    if (!url) {
      resolve({ width: 0, height: 0, fileName: '' });
    }
    const image = new Image();
    image.src = url;
    image.addEventListener('load', () => {
      resolve({
        width: image.width as number,
        height: image.height,
        fileName: getFilenameFromUrl(image.src),
      });
    });
  });
}

export function isNumber(str: string | number) {
  return str !== '' && !Number.isNaN(Number(str));
}

export function formatDate(
  timestamp: string | number | undefined,
  withSeconds = true,
  fallback = '',
) {
  if (!timestamp) {
    return fallback;
  }

  const timeString = isNumber(timestamp) ? parseInt(timestamp.toString(), 10) : timestamp;
  const date = new Date(timeString);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    timeStyle: !withSeconds ? 'short' : undefined,
  })}`;
}

export const pickUrlSearchParams = (searchParams: URLSearchParams, keys: string[]) => {
  const pickedSearchParams = new URLSearchParams();
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const value = searchParams.get(key);
    if (value) {
      pickedSearchParams.append(key, value);
    }
  }
  return pickedSearchParams;
};

/**
 * Used to retrieve the type of array
 */
export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export const getOrgUrl = (path: string, orgId?: number | string) => {
  const normalizedPath = path[0] !== '/' ? `/${path}` : path;

  if (orgId) {
    if (path.includes('organisationId')) {
      return normalizedPath;
    }

    const separator = path.includes('?') ? '&' : '?';
    return `${normalizedPath}${separator}organisationId=${orgId}`;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  const existingSearchParams = new URLSearchParams(window.location.search);
  const existingOrgId = existingSearchParams.get('organisationId');

  if (!existingOrgId || path.includes('organisationId')) {
    return normalizedPath;
  }

  const separator = path.includes('?') ? '&' : '?';
  return `${normalizedPath}${separator}organisationId=${existingOrgId}`;
};

export const linkIsSelected = (href: string) =>
  typeof window !== 'undefined' && window.location.href.includes(href);

export class SemanticVersion {
  major: number = 0;

  minor: number = 0;

  patch: number = 0;

  constructor(semVerString?: string | null) {
    if (semVerString) {
      const components = semVerString.split('.');
      this.major = parseInt(components[0], 10);
      this.minor = parseInt(components[1], 10);
      this.patch = parseInt(components[2], 10);
    }
  }

  gte(semVer: SemanticVersion) {
    return this.major >= semVer.major && this.minor >= semVer.minor && this.patch >= semVer.patch;
  }
}

/**
 * Convert the given camel case string to flat case.
 * This supports either Sentence Case (Hello world) or Title case (Hello World)
 *
 * @param {string} str Camel case string to convert
 * @param {boolean} sentenceCase whether to return in Sentence case or Title case
 */
export const camelToFlat = (str: string, sentenceCase = true) => {
  const flattedStr = str.replace(/[A-Z]/g, ' $&').trim();
  return sentenceCase
    ? flattedStr[0].toUpperCase() + flattedStr.slice(1).toLowerCase()
    : flattedStr;
};

/**
 * Return the timezone offset to UTC as a string -> (+10:00) UTC
 */
export const getTimezoneUTC = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const o = Math.abs(offset);
  return (
    (offset < 0 ? '+' : '-') +
    ('00' + Math.floor(o / 60)).slice(-2) +
    ':' +
    ('00' + (o % 60)).slice(-2).concat(' UTC')
  );
};

export const API_PREVIEW_VERSION = new SemanticVersion('0.0.103');

/**
 * Utility to handle browser database interactions (LocalStorage)
 */

export const IS_SERVER = typeof window === 'undefined';

export interface PreviewSchema {
  fields: TTSchemaField[];
  subSchemas?: PreviewSchema[];
}

/**
 * Recursively extracts all fields from a schema and its subschemas into a field map.
 */
const getSchemaFields = (schema: PreviewSchema, depth = 0): FieldMap => {
  if (depth > MAX_SCHEMA_RECURSION_DEPTH) {
    throw new Error('Schema loop detected while generating field map');
  }
  let fieldMap = schema.fields.reduce<Map<number, TTSchemaField>>((fieldMap, field) => {
    fieldMap.set(field.id, field);
    return fieldMap;
  }, new Map());
  if (schema.subSchemas) {
    fieldMap = schema.subSchemas.reduce((curSubSchemaFieldMap, subSchema) => {
      const subSchemaFieldMap = getSchemaFields(subSchema, depth + 1);
      subSchemaFieldMap.forEach((field, fieldId) => curSubSchemaFieldMap.set(fieldId, field));
      return curSubSchemaFieldMap;
    }, fieldMap);
  }
  return fieldMap;
};

/**
 * Builds a complete field map from all revision schemas.
 */
export const getRevisionSchemasFieldMap = (
  revisionSchemas: DashboardPageRevisionSchema[],
): FieldMap => {
  return revisionSchemas.reduce<Map<number, TTSchemaField>>(
    (curRevisionSchemaFields, revisionSchema) => {
      if (revisionSchema.ttschema) {
        const revisionSchemaFields = getSchemaFields(
          revisionSchema.ttschema as unknown as PreviewSchema,
        );
        revisionSchemaFields.forEach((field, fieldId) =>
          curRevisionSchemaFields.set(fieldId, {
            ...field,
            // GraphQL converts the schema field type to the enum key, but our
            // library compares to enum values
            type: TTSchemaFieldType[field.type as unknown as keyof typeof TTSchemaFieldType],
          }),
        );
      }
      return curRevisionSchemaFields;
    },
    new Map(),
  );
};

export type GenerateRevisionSchemaProps = {
  revision?: PageRevisionEntityDto | null;
  ttschema?: TTSchemaWithSubschemas;
  template?: TemplateEntityDto;
  campaign?: boolean;
  schemaBlockId?: number;
  label?: string;
  client: Client;
};

export const generateRevisionSchema = async ({
  client,
  ttschema,
  revision,
  template,
  schemaBlockId,
  label,
  campaign = false,
}: GenerateRevisionSchemaProps) => {
  let nextId: number = 1;
  let nextSortOrder: number = 1;
  if (Array.isArray(revision?.schema) && (revision?.schema || []).length > 0) {
    revision?.schema.forEach((schema: any) => {
      if (schema.id >= nextId) {
        nextId = schema.id + 1;
      }
      if (schema.sortOrder >= nextSortOrder) {
        nextSortOrder = schema.sortOrder + 1;
      }
    });
  }
  let _schemaBlockId = schemaBlockId;
  if (!schemaBlockId) {
    const { data } = await client
      .mutation<CreateSchemaBlockMutation, CreateSchemaBlockMutationVariables>(
        CREATE_SCHEMA_BLOCK,
        {},
      )
      .toPromise();
    if (data) {
      _schemaBlockId = data.createSchemaBlock.id;
    }
  }
  return {
    id: nextId,
    sortOrder: nextSortOrder,
    properties: [],
    ...(ttschema && {
      properties: generateFieldProperties(ttschema, ttschema.subSchemas),
    }),
    campaign,
    schemaBlockId: _schemaBlockId,
    ...(ttschema && { ttschema }),
    ...(template && { template }),
    ...(label && { label }),
  };
};

export const filterFieldsHiddenByVisibility =
  (isSiteAdmin: boolean) => (visibility?: TTSchemaFieldVisibility) => {
    if (!visibility) {
      return false;
    }
    if (isSiteAdmin) {
      return true;
    }
    return visibility === TTSchemaFieldVisibility.All;
  };

export const getValidationErrorsFromSchemas = (
  revisionSchemas: DashboardPageRevisionSchema[],
  isSiteAdmin: boolean,
  references: ReferenceList,
) => {
  // Validate schema properties before saving
  const fieldMap = getRevisionSchemasFieldMap(revisionSchemas);

  const validationErrors: ValidationError[] = [];
  for (const revisionSchema of revisionSchemas) {
    const schemaId = revisionSchema.ttschema?.id;
    const validationResult = validateFieldPropertySchemaValue(
      revisionSchema.properties,
      `${revisionSchema.schemaBlockId ?? ''}`,
      fieldMap,
      schemaId || 0,
      references,
    );
    validationErrors.push(...validationResult.errors);
  }

  const filterValidationErrors = filterFieldsHiddenByVisibility(isSiteAdmin);
  const errors = validationErrors
    .filter((error) => error.severity > ValidationErrorSeverity.WARNING)
    .filter((error) => {
      const field = fieldMap.get(error.fieldId!);
      return filterValidationErrors(field?.visibility);
    })
    .reduce<Map<string, ValidationError>>((map, err) => map.set(`${err.path}`, err), new Map());
  return errors;
};

export const getErrorsAtPath = (
  validationErrors: Map<string, ValidationError>,
  path: string,
): ValidationError[] => {
  const errors: ValidationError[] = [];
  validationErrors.forEach((error, errorPath) => {
    if (errorPath.startsWith(path)) {
      errors.push(error);
    }
  });
  return errors;
};

/**
 * Takes a list of validation errors and a path, and returns a list of indexes
 * where errors occur at that path.
 */
export const getErrorIndexesAtPath = (
  validationErrors: Map<string, ValidationError>,
  path: string,
): number[] => {
  const paths: string[] = [];
  Array.from(validationErrors.keys()).forEach((errorPath) => {
    if (errorPath.startsWith(path)) {
      paths.push(errorPath.replace(path, ''));
    }
  });
  return paths
    .map((p) => {
      const match = p.match(/^\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((index): index is number => index !== null);
};

/**
 * Converts yup validation errors to nested Formik errors object.
 * This allows useField to properly access errors for nested paths.
 */
export function yupErrorsToFormikErrors(errors: Array<yup.ValidationError>): Record<string, any> {
  const formikErrors: Record<string, any> = {};
  errors.forEach((error) => {
    if (error.path) {
      set(formikErrors, error.path, error.message);
    }
  });
  return formikErrors;
}
